import { type RefObject, useEffect, useRef } from 'react'
import { paginationCopy } from '@/copy'
import { formatNumber } from '@/utils/format'
import { cx } from '../cx'
import { Select } from '../Field/Field'
import { Icon } from '../Icon/Icon'
import { Spinner } from '../Loading/Loading'
import styles from './Pagination.module.css'

type Numbered = {
  page: number
  pageCount: number
  onPageChange: (page: number) => void
  total: number
  pageSize: number
  onPageSizeChange?: (size: number) => void
  pageSizeOptions?: readonly number[]
  resultsRef?: RefObject<HTMLElement | null>
}

type Cursor = {
  hasMore: boolean
  onLoadMore: () => void
  loading?: boolean
  loaded: number
}

export type PaginationProps = (Numbered | Cursor) & { className?: string }

/** First, last and a three-page run around the current page, with gaps. */
function pageWindow(page: number, count: number): Array<number | 'gap'> {
  const s = Math.min(Math.max(page - 1, 1), Math.max(count - 2, 1))
  const pages = [
    ...new Set([1, count, s, s + 1, s + 2].filter((p) => p >= 1 && p <= count)),
  ].sort((a, b) => a - b)
  const out: Array<number | 'gap'> = []
  pages.forEach((p, i) => {
    const prev = pages[i - 1]
    if (prev !== undefined) {
      if (p - prev === 2) out.push(prev + 1)
      else if (p - prev > 2) out.push('gap')
    }
    out.push(p)
  })
  return out
}

function NumberedPagination({
  page,
  pageCount,
  onPageChange,
  total,
  pageSize,
  onPageSizeChange,
  pageSizeOptions,
  resultsRef,
  className,
}: Numbered & { className?: string }) {
  const pendingFocus = useRef(false)

  // biome-ignore lint/correctness/useExhaustiveDependencies: runs once per page change
  useEffect(() => {
    if (!pendingFocus.current) return
    resultsRef?.current?.focus({ preventScroll: false })
    pendingFocus.current = false
  }, [page, resultsRef])

  if (pageCount < 2) return null

  const go = (n: number) => {
    // Focus moves only after a real page change; the current page is a no-op.
    if (n === page) return
    pendingFocus.current = true
    onPageChange(n)
  }
  const start = (page - 1) * pageSize + 1
  const end = Math.min(page * pageSize, total)

  return (
    <nav
      aria-label={paginationCopy.label}
      className={cx(styles.nav, className)}
    >
      <button
        type="button"
        className={styles.control}
        data-arrow="previous"
        aria-label={paginationCopy.previous}
        disabled={page <= 1}
        onClick={() => go(page - 1)}
      >
        <Icon name="chevron-right" />
      </button>
      {pageWindow(page, pageCount).map((p, i) =>
        p === 'gap' ? (
          // biome-ignore lint/suspicious/noArrayIndexKey: gaps have no identity
          <span key={`gap-${i}`} className={styles.gap} aria-hidden="true">
            …
          </span>
        ) : (
          <button
            key={p}
            type="button"
            className={styles.control}
            aria-current={p === page ? 'page' : undefined}
            onClick={() => go(p)}
          >
            <span>{formatNumber(p)}</span>
          </button>
        ),
      )}
      <button
        type="button"
        className={styles.control}
        data-arrow="next"
        aria-label={paginationCopy.next}
        disabled={page >= pageCount}
        onClick={() => go(page + 1)}
      >
        <Icon name="chevron-right" />
      </button>
      {onPageSizeChange && pageSizeOptions && (
        // biome-ignore lint/a11y/noLabelWithoutControl: Select wraps a native select
        <label className={styles.size}>
          <span>{paginationCopy.pageSize}</span>
          <Select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
          >
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>
                {formatNumber(n)}
              </option>
            ))}
          </Select>
        </label>
      )}
      <span className={styles.count} role="status">
        {paginationCopy.range(
          formatNumber(start),
          formatNumber(end),
          formatNumber(total),
        )}
      </span>
    </nav>
  )
}

function CursorPagination({
  hasMore,
  onLoadMore,
  loading,
  loaded,
  className,
}: Cursor & { className?: string }) {
  return (
    <nav
      aria-label={paginationCopy.label}
      className={cx(styles.nav, className)}
    >
      {hasMore && (
        <button
          type="button"
          className={styles.control}
          data-kind="more"
          aria-busy={loading || undefined}
          aria-disabled={loading || undefined}
          onClick={() => {
            if (loading) return
            onLoadMore()
          }}
        >
          {loading && <Spinner tone="current" />}
          {paginationCopy.loadMore}
        </button>
      )}
      <span className={styles.count} role="status">
        {paginationCopy.loaded(formatNumber(loaded))}
      </span>
    </nav>
  )
}

export function Pagination(props: PaginationProps) {
  return 'page' in props ? (
    <NumberedPagination {...props} />
  ) : (
    <CursorPagination {...props} />
  )
}
